import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getVenue } from '../../shared/api/venueApi';
import { VenueData } from '../../shared/dto/EntityRepository';
import { PIInput } from '../../shared/component/headless/input/InputBody';
import { PROJECT_INFO_VALUE_MAX_LENGTH } from '../../shared/constants/limits';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getProjectInfoLinkLabel } from '../../shared/util/projectInfo';

interface ProjectInfoLinkedInputProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  venueId: number | null;
  url: string | null;
}

const ProjectInfoLinkedInput: React.FC<ProjectInfoLinkedInputProps> = ({ value, placeholder, onChange, venueId, url }) => {
  const [venue, setVenue] = useState<VenueData | null>(null);
  const setTextLengthExceededAlert = useStandardAlertStore((s) => s.setTextLengthExceededAlert);

  useEffect(() => {
    if (venueId == null) return;
    let cancelled = false;

    getVenue(String(venueId))
      .then(({ data }) => {
        if (!cancelled) setVenue(data);
      })
      .catch(() => {
        if (!cancelled) setVenue(null);
      });

    return () => { cancelled = true; };
  }, [venueId]);

  const venueName = venue && String(venue.id) === String(venueId)
    ? venue.name.trim() || venue.nameEn.trim() || venue.venueCode
    : 'Venue';
  const label = venueId != null ? venueName : getProjectInfoLinkLabel(url);

  return (
    <Container>
      <TextLength aria-hidden="true">{value.length} / {PROJECT_INFO_VALUE_MAX_LENGTH}</TextLength>
      <InputRow>
        <InputSizer>
          <InputMeasure aria-hidden="true">{value || placeholder}{'\u00a0'}</InputMeasure>
          <ValueInput
            type="text"
            value={value}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(event) => {
              if (event.target.value.length > PROJECT_INFO_VALUE_MAX_LENGTH) {
                setTextLengthExceededAlert(PROJECT_INFO_VALUE_MAX_LENGTH);
                return;
              }
              onChange(event.target.value);
            }}
          />
        </InputSizer>
        {label && <LinkLabel>(<LinkText>{label}</LinkText>)</LinkLabel>}
      </InputRow>
    </Container>
  );
};

export default ProjectInfoLinkedInput;

const Container = styled.label`
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  cursor: text;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const TextLength = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
`;

const InputRow = styled.span`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  padding-top: 14px;
  padding-bottom: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const InputSizer = styled.span`
  position: relative;
  flex: 0 0 auto;
  max-width: 70%;
  min-width: 1ch;
`;

const InputMeasure = styled.span`
  display: block;
  visibility: hidden;
  white-space: pre;
`;

const ValueInput = styled(PIInput)`
  position: absolute;
  inset: 0;
  min-width: 0;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  font: inherit;
`;

const LinkLabel = styled.span`
  display: inline-flex;
  flex: 0 1 auto;
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const LinkText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
